// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Intervals } from '@shared/utils/Instruments/Intervals';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { DynProperty } from '../../../DynProperty';
import { NumericUtils } from '@shared/utils/NumericUtils';
import { OrderEditUpdateData } from '@shared/utils/Trading/OrderEditUpdateData';
import { SLTPEdit } from '../SLTPEdit';
import { OrderEditBaseUtils } from '@shared/utils/Trading/OrderEditBaseUtils';
import { OrderEditBase } from './OrderEditBase';
import { BaseSettings } from '../../../Settings/BaseGeneralSettingsWrapper';
export class StopLimitOrderEdit extends OrderEditBase {
    constructor(data) {
        super(data);
        // TODO. Ugly. Refactor. Remove. http://tp.pfsoft.net/entity/76728
        this.needSetDefaultStopPrice = false;
        this.needSetDefaultLimitPrice = false;
        // Remove https://tp.traderevolution.com/entity/82704
        // data.fullRangeForLimitPrice || false
        // this.forceCorrectLimitPrice = false
        this.fullRangeForLimitPrice = true;
        this.DataCache = data.dataCache;
        this.sltp = new SLTPEdit(data.dataCache, data.sltpMode);
        this.sltpBasePriceGetter = this.sltpBasePriceGetter.bind(this);
        this.DataCache.OnActivateOrder.Subscribe(this.updateStopPriceWhenActivate, this); // #93951
    }
    getParameterNameArray() {
        return [
            OrderEditBaseUtils.STOP_PRICE_PARAM,
            OrderEditBaseUtils.LIMIT_PRICE_PARAM,
            OrderEditBaseUtils.POSITION_SIZING_PARAM,
            OrderEditBaseUtils.SLTP_PARAM,
            OrderEditBaseUtils.OFFMARKET_ENABLED,
            OrderEditBaseUtils.EST_AVAILABLE_CASH
        ];
    }
    // TODO. Refactor. Same as OrderTypeBase.Id function.
    getOrderTypeId() {
        return OrderType.StopLimit;
    }
    // TODO. Refactor. Use error/warning dicts for sltp.
    // public valid ()
    // {
    //     return OrderEditBase.prototype.valid.call(this) &&
    //         this.sltp.valid()
    // }
    dispose() {
        this.DataCache.OnActivateOrder.UnSubscribe(this.updateStopPriceWhenActivate, this);
        super.dispose();
    }
    updateStopPriceWhenActivate() {
        // #93951
        this.ParametersChanged.Raise({ stopPrice: this.toDynProperty_stopPrice() });
    }
    // #region Parameters' DynProperty Generators
    toDynProperty_stopPrice() {
        var _a;
        const dp = new DynProperty(OrderEditBaseUtils.STOP_PRICE_PARAM);
        dp.type = DynProperty.DOUBLE;
        const ins = this.instrument;
        const intrvls = Intervals.GetIntervalsFromInstrument(ins, NumericUtils.MAXVALUE);
        if (ins) {
            dp.minimalValue = intrvls && intrvls.length > 0 ? intrvls[0].LeftValue : ins.PointSize;
            dp.maximalValue = 999999999;
            dp.decimalPlaces = ins.Precision;
            dp.increment = ins.PointSize;
        }
        if (intrvls) {
            dp.Intervals = intrvls;
        }
        dp.value = this.stopPrice;
        if (dp.value === null || isNaN(dp.value)) {
            dp.value = dp.minimalValue;
        }
        if ((_a = this.order) === null || _a === void 0 ? void 0 : _a.IsActivated) {
            dp.enabled = false;
        } // #92963
        dp.localizationKey = 'panel.newOrderEntry.stopPricePanel';
        return dp;
    }
    toDynProperty_limitPrice() {
        const dp = new DynProperty(OrderEditBaseUtils.LIMIT_PRICE_PARAM);
        dp.type = DynProperty.DOUBLE;
        const ins = this.instrument;
        const intrvls = Intervals.GetIntervalsFromInstrument(ins, NumericUtils.MAXVALUE);
        if (ins) {
            const pointSize = ins.PointSize;
            const basePrice = this.stopPrice || pointSize;
            // TODO. Ugly. Refactor. Remove. http://tp.pfsoft.net/entity/76728
            if (this.fullRangeForLimitPrice) {
                dp.minimalValue = intrvls ? intrvls[0].LeftValue : pointSize;
                dp.maximalValue = 999999999;
            }
            else if (this.buy()) {
                dp.minimalValue = intrvls ? intrvls[0].LeftValue : basePrice + pointSize;
                dp.maximalValue = 999999999;
            }
            else {
                dp.minimalValue = intrvls ? intrvls[0].LeftValue : pointSize;
                dp.maximalValue = basePrice - pointSize;
            }
            dp.decimalPlaces = ins.Precision;
            dp.increment = ins.PointSize;
        }
        if (intrvls) {
            dp.Intervals = intrvls;
        }
        dp.value = this.limitPrice;
        if (dp.value === null || isNaN(dp.value)) {
            dp.value = dp.minimalValue;
        }
        dp.localizationKey = 'panel.newOrderEntry.limitPricePanel';
        return dp;
    }
    // #endregion Parameters' DynProperty Generators
    // #region Raw Values
    toRawValue_limitPrice() {
        return this.limitPrice;
    }
    toRawValue_stopPrice() {
        return this.stopPrice;
    }
    // #endregion Raw Values
    // #region Update Parameter Handlers
    update_limitPrice(updateData) {
        return this.updatePrice(false, updateData);
    }
    update_stopPrice(updateData) {
        return this.updatePrice(true, updateData);
    }
    updatePrice(isStopPrice, updateData) {
        var _a, _b;
        let parameterChanged = false;
        const dp = updateData.dp;
        const price = isStopPrice ? this.stopPrice : this.limitPrice;
        if (dp && price !== dp.value && !(isNaN(price) && isNaN(dp.value))) {
            if (isStopPrice) {
                this.stopPrice = dp.value;
            }
            else {
                this.limitPrice = dp.value;
            }
            parameterChanged = true || parameterChanged;
        }
        let newInstrument = null;
        let newQuote = null;
        let sideChanged = false;
        const newTradingDataDict = updateData.newTradingDataDict;
        if (newTradingDataDict) {
            newInstrument = newTradingDataDict.instrument;
            newQuote = newTradingDataDict.quote;
            sideChanged = 'side' in newTradingDataDict;
        }
        if (newInstrument) {
            if (isStopPrice) {
                this.stopPrice = null;
                this.needSetDefaultStopPrice = true;
            }
            else {
                this.limitPrice = null;
                this.needSetDefaultLimitPrice = true;
            }
            parameterChanged = true || parameterChanged;
        }
        const needSetDefaultPrice = isStopPrice ? this.needSetDefaultStopPrice : this.needSetDefaultLimitPrice;
        if (needSetDefaultPrice && newQuote) {
            if (isStopPrice) {
                this.needSetDefaultStopPrice = false;
                this.stopPrice = newQuote.BidSpread_SP_Ins((_a = this.instrument) === null || _a === void 0 ? void 0 : _a.DataCache.GetSpreadPlan(this.account), this.instrument);
            }
            else {
                this.needSetDefaultLimitPrice = false;
                this.limitPrice = newQuote.BidSpread_SP_Ins((_b = this.instrument) === null || _b === void 0 ? void 0 : _b.DataCache.GetSpreadPlan(this.account), this.instrument);
            }
            parameterChanged = true || parameterChanged;
        }
        if (isStopPrice && (newQuote || sideChanged || parameterChanged)) {
            parameterChanged = this.tryUpdateWarning_stopPrice(this.stopPrice, this.quote) || parameterChanged;
        }
        return parameterChanged;
    }
    // TODO.
    tryUpdateWarning_stopPrice(stopPrice, quote) {
        var _a;
        if (!quote)
            return false;
        const order = this.order;
        if (order === null || order === void 0 ? void 0 : order.IsActivated)
            return false; // #93925
        const sp = (_a = this.instrument) === null || _a === void 0 ? void 0 : _a.DataCache.GetSpreadPlan(this.account);
        const ask = quote.AskSpread_SP_Ins(sp, this.instrument);
        const bid = quote.BidSpread_SP_Ins(sp, this.instrument);
        const buy = this.buy();
        const newWarning = buy && stopPrice < ask
            ? 'general.trading.stopBuyMoreAsk'
            : !buy && stopPrice > bid
                ? 'general.trading.stopSellLessBid'
                : null;
        return this.warningsManager.setParameterWarning(OrderEditBaseUtils.STOP_PRICE_PARAM, newWarning);
    }
    // #endregion Update Parameter Handlers
    // TODO.
    validateParameters() {
        const updatedParamNameDict = {};
        // // TODO. Ugly. Refactor. Remove. http://tp.pfsoft.net/entity/76728
        // Remove https://tp.traderevolution.com/entity/82704
        // if ((this.forceCorrectLimitPrice || !this.fullRangeForLimitPrice) &&
        //     this.tryCorrectLimitPrice())
        //     updatedParamNameDict[OrderEditBase.LIMIT_PRICE_PARAM] = true
        // TODO. Ugly. Refactor. Remove. http://tp.pfsoft.net/entity/76728
        // Remove https://tp.traderevolution.com/entity/82704
        // this.forceCorrectLimitPrice = false
        if (this.sltp.validate(this.sltpBasePriceGetter, this.sltpBasePriceGetter, this.getTradingData())) {
            updatedParamNameDict[OrderEditBaseUtils.SLTP_PARAM] = true;
        }
        updatedParamNameDict[OrderEditBaseUtils.POSITION_SIZING_PARAM] = true;
        return updatedParamNameDict;
    }
    // Fixing limit price relative to stop price
    // Returns 'true' if limitPrice is changed.
    tryCorrectLimitPrice() {
        const ins = this.instrument;
        if (!ins)
            return false;
        const pointSize = ins.PointSize;
        const stopPrice = this.stopPrice || pointSize;
        const curLimitPrice = this.limitPrice || pointSize;
        if (this.buy()) {
            const minLimitPrice = stopPrice + pointSize;
            if (curLimitPrice < minLimitPrice) {
                this.limitPrice = minLimitPrice;
                return true;
            }
        }
        else {
            const maxLimitPrice = stopPrice === pointSize ? pointSize : stopPrice - pointSize;
            if (curLimitPrice > maxLimitPrice) {
                this.limitPrice = maxLimitPrice;
                return true;
            }
        }
        return false;
    }
    // #region SLTP Price Comparer
    sltpBasePriceGetter() {
        return this.limitPrice;
    }
    // #endregion SLTP Price Comparer
    createConfirmationTextParams() {
        const textParams = super.createConfirmationTextParams();
        textParams.OrderTypeKey = 'Stop Limit';
        textParams.PriceArray = [this.stopPrice, this.limitPrice];
        textParams.OrderType = OrderType.StopLimit;
        return textParams;
    }
    // Returns SlTpHolder instance.
    // TODO. UGLY. Refactor. details are at the top OrderEditBase.ts
    getRawSLTP() {
        return this.sltp.getRawValue(this.getTradingData());
    }
    // TODO. Refactor. Details are in OrderEditBase.ts.
    setBasePrice(price, isLimitModify, noNeedToSetDefaultOffset = null) {
        if (isLimitModify) {
            const dp = this.createDynPropertyFromParameter(OrderEditBaseUtils.LIMIT_PRICE_PARAM);
            dp.value = price;
            this.updateParameters(new OrderEditUpdateData({ limitPrice: dp }));
            return;
        }
        let dp = this.createDynPropertyFromParameter(OrderEditBaseUtils.STOP_PRICE_PARAM);
        const startPrice = dp.value;
        dp.value = price;
        this.updateParameters(new OrderEditUpdateData({ stopPrice: dp }));
        const delta = price - startPrice;
        dp = this.createDynPropertyFromParameter(OrderEditBaseUtils.LIMIT_PRICE_PARAM);
        dp.value += delta;
        noNeedToSetDefaultOffset = false || noNeedToSetDefaultOffset;
        if (BaseSettings.isUseStopLimitInsteadStop() && !noNeedToSetDefaultOffset) {
            dp.value = this.getLimitPriceWithDefaultOffset(price);
        }
        this.updateParameters(new OrderEditUpdateData({ limitPrice: dp }));
    }
    setStopPrice(price) {
        const dp = this.createDynPropertyFromParameter(OrderEditBaseUtils.STOP_PRICE_PARAM);
        dp.value = price;
        this.updateParameters(new OrderEditUpdateData({ stopPrice: dp }));
    }
    setLimitPrice(price) {
        const dp = this.createDynPropertyFromParameter(OrderEditBaseUtils.LIMIT_PRICE_PARAM);
        dp.value = price;
        this.updateParameters(new OrderEditUpdateData({ limitPrice: dp }));
    }
    // TODO. Refactor. Details are in OrderEditBase.ts.
    getBasePrice() {
        return this.limitPrice;
    }
    setBreakevenPrice(price) {
        // вызывается при нажатии на стрелочку-кнопку QuotePricePicker a.k.a Breakeven
        const order = this.order;
        if (!order)
            return;
        if (order.IsActivated) {
            this.setBasePrice(price, true);
        }
        else {
            this.setStopPrice(price);
        }
    }
    skipParamForFlashing(paramName) {
        // при включенном positionSizing надо ли DynPropertyControl c paramName пропускать при подсветке лейблов (к примеру как Stop price не подсвечиваем у Stop limit-a)
        if (paramName === OrderEditBaseUtils.STOP_PRICE_PARAM) {
            return true;
        }
        return false;
    }
}
